import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtom } from 'jotai';
import { eSensorChartAtom, DeviceEUIProps, sensorInfoAtom } from '../atoms-types/eSensors';
import moment, { Moment } from 'moment-timezone';
import { calculateTimeIntervals, formatTimeLabel, shouldShowTimeLabel } from '../converters/timespanConverter';

interface SensorGraphProps extends DeviceEUIProps {
  startDateTime: Moment;
  endDateTime: Moment;
}

const SensorGraph: React.FC<SensorGraphProps> = ({ deviceEUI, Display, startDateTime, endDateTime }) => {
  const [esensorChart] = useAtom(eSensorChartAtom);
  const [sensorInfo] = useAtom(sensorInfoAtom);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  // Get current sensor data for real-time updates
  const getCurrentSensorData = () => {
    if (!deviceEUI) return null;
    
    const currentSensorInfo = sensorInfo[deviceEUI];
    if (!currentSensorInfo || currentSensorInfo.length === 0) return null;
    
    const sensor = currentSensorInfo[0];
    if (!sensor.jInfo) return null;
    
    const timestamp = (sensor.jInfo as any).Timestamp || new Date().toISOString();
    const jInfoObj = sensor.jInfo as Record<string, any>;
    
    // Create an object with all matching display values
    const displayValues = Array.isArray(Display) 
      ? Display.reduce((acc, key) => {
          const value = jInfoObj[key] ?? null;
          acc[key] = typeof value === 'number' ? value : 
                    (typeof value === 'string' && !isNaN(Number(value))) ? Number(value) : null;
          return acc;
        }, {} as Record<string, number | null>)
      : { [Display]: typeof jInfoObj[Display] === 'number' ? jInfoObj[Display] :
                 (typeof jInfoObj[Display] === 'string' && !isNaN(Number(jInfoObj[Display]))) ? 
                 Number(jInfoObj[Display]) : null };

    return {
      timestamp,
      ...displayValues
    };
  };

  // Combine historical data with current real-time data
  const getCombinedChartData = () => {
    const currentData = getCurrentSensorData();
    if (!currentData) return esensorChart;
    
    // Check if current data already exists in historical data
    const exists = esensorChart.some(item => item.timestamp === currentData.timestamp);
    if (exists) return esensorChart;
    
    // Add current data to the end
    return [...esensorChart, currentData as any];
  };

  const chartData = getCombinedChartData();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      const chartElement = document.querySelector('.echarts-for-react');
      if (chartElement) {
        const echartsInstance = (chartElement as any).__ECharts__;
        if (echartsInstance) {
          echartsInstance.resize();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const timestamps: string[] = [];
  const verticalLines: any[] = [];
  let previousDate = "";
  let minRiverHeight = 220;

  // Calculate time intervals based on timespan
  const { interval, showHalfHours } = calculateTimeIntervals(startDateTime, endDateTime);

  // Create a map of timestamps to their indices
  const timestampMap = new Map<string, number>();
  chartData.forEach((item, index) => {
    const timestamp = moment.utc(item.timestamp).tz('America/New_York');
    const formattedTime = formatTimeLabel(timestamp, showHalfHours);
    const currentDate = timestamp.format('M/D');

    timestamps.push(formattedTime);
    timestampMap.set(formattedTime, index);

    // Track minimum RiverHeight value
    if ((item as any).Height !== null && (item as any).Height !== undefined) {
      minRiverHeight = Math.min(minRiverHeight, Number((item as any).Height));
    }

    // Add vertical lines for date changes
    if (currentDate !== previousDate && timestamp.hour() === 0 && timestamp.minute() === 0) {
      verticalLines.push({
        xAxis: index,
        lineStyle: {
          color: 'lightgrey',
          type: 'dashed',
        },
        label: {
          formatter: currentDate,
          position: 'end',
          fontSize: 10,
          color: 'grey',
        },
        tooltip: {
          formatter: `Date: ${currentDate}`,
        },
      });
      previousDate = currentDate;
    }
  });

  const colors = ['orange','seagreen', 'steelblue', 'slateblue', 'firebrick', 'purple', 'crimson', 'gold'];
  
  // Define RiverHeight thresholds
  const riverHeightThresholds = {
    overDam: 217.74,
    overPathDam: 215.56,
    overPathDam2: 212.36,
    overPath11: 211.28
  };

  const series = Array.isArray(Display) ? Display.map((display, index) => {
    const values = chartData.map((item) => (item as any)[display]);
    const color = colors[index % colors.length];

    // Special handling for RiverHeight
    if (display === 'Height') {
      return {
        name: 'River Height',
        type: 'line',
        data: values.map(value => ({
          value: value,
          itemStyle: {
            color: '#1E88E5'  // Keep the dots blue to match the line
          }
        })),
        lineStyle: {
          width: 2,
          color: '#1E88E5'  // A nice blue color for the line
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(30, 136, 229, 0.2)'  // Semi-transparent blue
              },
              {
                offset: 1,
                color: 'rgba(30, 136, 229, 0.2)'
              }
            ]
          }
        },
        smooth: true,
        tooltip: {
          valueFormatter: function (value: number | null | undefined) {
            if (value === null || value === undefined) {
              return 'No data';
            }
            return Number(value).toFixed(2);
          },
        },
        markLine: {
          symbol: 'none',
          data: [
            {
              yAxis: riverHeightThresholds.overDam,
              lineStyle: { 
                color: 'firebrick',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Dam',
                position: 'end',
                fontSize: 10,
                color: 'firebrick'
              }
            },
            {
              yAxis: riverHeightThresholds.overPathDam,
              lineStyle: { 
                color: 'orange',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Dam Path',
                position: 'end',
                fontSize: 10,
                color: 'orange'
              }
            },
            {
              yAxis: riverHeightThresholds.overPathDam2,
              lineStyle: { 
                color: 'gold',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Dam on 18',
                position: 'end',
                fontSize: 10,
                color: 'gold'
              }
            },
            {
              yAxis: riverHeightThresholds.overPath11,
              lineStyle: { 
                color: 'seagreen',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Path at 11',
                position: 'end',
                fontSize: 10,
                color: 'seagreen'
              }
            },
            ...verticalLines
          ]
        }
      };
    }

    return {
      name: display,
      type: 'line',
      data: values.map(value => ({
        value: value,
        itemStyle: {
          color: color
        }
      })),
      lineStyle: {
        color: color,
      },
      itemStyle: {
        color: color,
      },
      smooth: true,
      tooltip: {
        valueFormatter: function (value: number | null | undefined) {
          if (value === null || value === undefined) {
            return 'No data';
          }
          return Number(value).toFixed(2);
        },
      },
      // Only add vertical date lines to the first series
      ...(index === 0 ? {
        markLine: {
          symbol: 'none',
          data: verticalLines,
        }
      } : {})
    };
  }) : [];

  const option = {
    tooltip: {
      show: !isMobile,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
      formatter: function(params: any) {
        const timestamp = moment.utc(chartData[params[0].dataIndex].timestamp).tz('America/New_York');
        const formattedTime = timestamp.format('MM/DD/YYYY h:mm:ss A');
        let result = `${formattedTime}<br/>`;
        params.forEach((param: any) => {
          const value = param.value === null || param.value === undefined ? 'No data' : Number(param.value).toFixed(2);
          const color = param.color;
          result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${param.seriesName}: ${value}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [
        ...series.map(s => s.name),
        ...(Array.isArray(Display) && Display.includes('Height') ? [
        ] : [])
      ],
      textStyle: {
        color: 'grey',
      },
      bottom: 0,
      padding: [5, 0, 0, 0],
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    toolbox: {
      feature: {
        saveAsImage: {},
      },
    },
    xAxis: {
      type: 'category',
      data: timestamps,
      axisLabel: {
        color: 'text.secondary',
        rotate: 45,
        interval: (index: number) => {
          const timestamp = moment.utc(chartData[index].timestamp).tz('America/New_York');
          return shouldShowTimeLabel(timestamp, startDateTime, interval, showHalfHours);
        },
        formatter: (value: string) => {
          const timestamp = moment.utc(chartData[timestamps.indexOf(value)].timestamp).tz('America/New_York');
          return formatTimeLabel(timestamp, showHalfHours);
        }
      },
      name: 'Time',
      nameTextStyle: {
        color: 'grey',
      },
    },
    yAxis: {
      type: 'value',
      name: 'Value',
      axisLabel: {
        formatter: '{value}',
        color: 'text.secondary',
      },
      nameTextStyle: {
        color: 'grey',
      },
      ...(Array.isArray(Display) && Display.includes('Height') ? {
        min: Math.max(200, minRiverHeight - 5),
        max: 220
      } : {})
    },
    series: series,
  };

  useEffect(() => {
    const chartElement = document.querySelector('.echarts-for-react');
    if (chartElement) {
      const echartsInstance = (chartElement as any).__ECharts__;
      if (echartsInstance) {
        echartsInstance.clear();
        echartsInstance.setOption(option);
      }
    }
  }, [Display, chartData]);

  return (
    <ReactECharts
      key={`${deviceEUI}-${Array.isArray(Display) ? Display.join('-') : Display}`}
      option={option}
      className="cancelSelectorName"
      style={{ height: '400px', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default SensorGraph;
